// components/MyProduct/Common/ImageUploadPanel.tsx
import React from 'react';
import { Button, Upload, Radio, Space, Input } from 'antd';
import { UploadOutlined, LinkOutlined, CodeOutlined } from '@ant-design/icons';
import {ImageType} from "@components/MyProduct/Common/useImageCheck";

export interface ImageInfo {
    name: string;
    size: number;
    lastModified: string;
    type: string;
    dimensions?: { width: number; height: number };
}

export interface ImageUploadPanelProps {
    // 基本属性
    fileList: any[];
    imagePreview: string;
    imageInfo: ImageInfo | null;
    onUpload: (params: any) => void;
    onRemove: () => void;

    // 图片类型相关
    imageType?: ImageType;
    onImageTypeChange?: (type: ImageType) => void;
    imageUrl?: string;
    onImageUrlChange?: (url: string) => void;

    // 其他配置
    accept?: string;
    buttonText?: string;
    formatFileSize: (bytes: number) => string;
    showImageTypeSelector?: boolean;
    showImageUrlInput?: boolean;
}

export const ImageUploadPanel: React.FC<ImageUploadPanelProps> = ({
                                                                      fileList,
                                                                      imagePreview,
                                                                      imageInfo,
                                                                      onUpload,
                                                                      onRemove,
                                                                      imageType = 'BASE64',
                                                                      onImageTypeChange,
                                                                      imageUrl = '',
                                                                      onImageUrlChange,
                                                                      accept = 'image/*',
                                                                      buttonText = '选择图片文件',
                                                                      formatFileSize,
                                                                      showImageTypeSelector = false,
                                                                      showImageUrlInput = false,
                                                                  }) => {
    return (
        <div style={{ marginBottom: 24 }}>
            {/* 图片类型选择器 */}
            {showImageTypeSelector && onImageTypeChange && (
                <div style={{ marginBottom: 16  ,marginTop: 5 }}>
                    <Radio.Group
                        value={imageType}
                        onChange={(e) => onImageTypeChange(e.target.value)}
                        style={{ display: 'flex', gap: '16px' }}
                    >
                        <Radio value="BASE64">
                            <Space>
                                <CodeOutlined />
                                <span>上传图片</span>
                            </Space>
                        </Radio>
                        <Radio value="URL">
                            <Space>
                                <LinkOutlined />
                                <span>图片路径</span>
                            </Space>
                        </Radio>
                    </Radio.Group>
                </div>
            )}

            {/* 图片URL输入框 */}
            {showImageUrlInput && imageType === 'URL' && onImageUrlChange && (
                <div style={{ marginBottom: 16 }}>
                    <Input.TextArea
                        value={imageUrl}
                        onChange={(e) => onImageUrlChange(e.target.value)}
                        placeholder="请输入图片URL地址，如：https://example.com/image.jpg"
                        rows={3}
                        style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            outline: 'none',
                            resize: 'vertical',
                        }}
                    />
                    <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                        请输入完整的图片URL地址，确保图片可公开访问
                    </div>
                </div>
            )}

            {/* 图片上传区域（只在BASE64模式显示） */}
            {imageType === 'BASE64' && (
                <div style={{ marginBottom: 8 }}>
                    <Upload
                        accept={accept}
                        showUploadList={false}
                        customRequest={onUpload}
                        fileList={fileList}
                        maxCount={1}
                    >
                        <Button type="primary" ghost icon={<UploadOutlined />}>
                            {buttonText}
                        </Button>
                    </Upload>
                    <span style={{ marginLeft: 12, fontSize: 12, color: '#999' }}>
            支持 JPG、PNG、BMP、GIF 格式，最大 2MB
          </span>
                </div>
            )}

            {/* 图片信息预览 */}
            {imageInfo && imagePreview && imageType === 'BASE64' && (
                <div style={{
                    marginTop: 12,
                    padding: 16,
                    border: '1px dashed #d9d9d9',
                    borderRadius: 6,
                    backgroundColor: '#fafafa'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>图片信息</div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                                <strong>文件名：</strong>
                                <span style={{ fontFamily: 'monospace' }}>{imageInfo.name}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                                <strong>文件大小：</strong>
                                <span>{formatFileSize(imageInfo.size)}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                                <strong>修改时间：</strong>
                                <span>{imageInfo.lastModified}</span>
                            </div>
                            {imageInfo.dimensions && (
                                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                                    <strong>图片尺寸：</strong>
                                    <span>{imageInfo.dimensions.width} × {imageInfo.dimensions.height} 像素</span>
                                </div>
                            )}
                            <div style={{ fontSize: 12, color: '#666' }}>
                                <strong>文件类型：</strong>
                                <span>{imageInfo.type}</span>
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                width: 120,
                                height: 120,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                borderRadius: 8,
                                border: '1px solid #e8e8e8'
                            }}>
                                <img
                                    src={imagePreview}
                                    alt="预览"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: 8 }}>
                                <Button type="link" danger onClick={onRemove} size="small">
                                    移除
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};